import {
  Paths,
  TalentSearchQueryParams,
  Option,
  ItemWithOptions,
  KeywordsResponse,
  SavedSearchParams,
  UpdateSavedSearchParams,
  SearchItem,
  FilterOptions,
} from '../shared/types';
import {
  standardRequest,
  standardRequestWithFlash,
  standardRequestWithErrorFlash,
} from '../../shared/utils/request';
import { withLocalePrefix } from 'shared/utils/translation';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import URI from 'urijs';

const t = withLocalePrefix('talent_search');

class Api {
  candidateTagsPath: string;
  customFieldsPath: string;
  countsPath: string;
  describeFieldOptionsPath: string;
  cacheParamsPath?: string;
  schoolsPath: string;
  degreesPath: string;
  disciplinesPath: string;
  jobKeywordsPath: string | null;
  applicationFieldsPath: string;
  rejectionReasonOptionsPath?: string;
  departmentsPath?: string;
  officesPath?: string;
  jobsPath?: string;
  prospectPoolsPath?: string;
  prospectPoolStagesPath?: string;
  prospectOwnerOptionsPath?: string;
  sourceOptionsPath?: string;
  eventOptionsPath?: string;
  rediscoveryKeywordsPath?: string;
  stagesPath?: string;

  savedSearchesPath?: string;
  scorecardAttributesPath?: string;
  constructor(paths: Paths) {
    this.candidateTagsPath = paths.candidateTagsPath;
    this.customFieldsPath = paths.customFieldsPath;
    this.countsPath = paths.countsPath;
    this.describeFieldOptionsPath = paths.describeFieldOptionsPath;
    this.cacheParamsPath = paths.cacheParamsPath;
    this.schoolsPath = paths.schoolsPath;
    this.degreesPath = paths.degreesPath;
    this.disciplinesPath = paths.disciplinesPath;
    this.jobKeywordsPath = paths.jobKeywordsPath;
    this.applicationFieldsPath = paths.applicationFieldsPath;
    this.rejectionReasonOptionsPath = paths.rejectionReasonOptionsPath;
    this.departmentsPath = paths.departmentsPath;
    this.officesPath = paths.officesPath;
    this.jobsPath = paths.jobsPath;
    this.prospectPoolsPath = paths.prospectPoolsPath;
    this.prospectPoolStagesPath = paths.prospectPoolStagesPath;
    this.prospectOwnerOptionsPath = paths.prospectOwnerOptionsPath;
    this.sourceOptionsPath = paths.sourceOptionsPath;
    this.eventOptionsPath = paths.eventOptionsPath;
    this.rediscoveryKeywordsPath = paths.rediscoveryKeywordsPath;
    this.stagesPath = paths.stagesPath;
    this.savedSearchesPath = paths.savedSearchesPath;
    this.scorecardAttributesPath = paths.scorecardAttributesPath;
  }

  fetchTags = async (term: string): Promise<Item[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.candidateTagsPath,
      {
        params: {
          term: term,
          react_select: true,
          per_page: 200,
        },
      }
    );

    return response.data.results;
  };

  fetchRejectionReasons = async (term: string): Promise<ItemWithOptions[]> => {
    if (!this.rejectionReasonOptionsPath) {
      return [];
    }

    const response = await standardRequestWithErrorFlash.get(
      this.rejectionReasonOptionsPath,
      {
        params: { term },
      }
    );

    return response.data.results;
  };

  fetchCustomFields = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.customFieldsPath,
      {
        params: {
          term: term,
          per_page: 200,
          omit_education: true,
        },
      }
    );

    return response.data.results;
  };

  fetchApplicationCustomFields = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.applicationFieldsPath,
      {
        params: {
          term: term,
          per_page: 200,
          omit_education: true,
        },
      }
    );

    return response.data.results;
  };

  fetchCustomFieldOptions = async (id: string): Promise<Option[]> => {
    const url = this.describeFieldOptionsPath.replace(':id', id);
    const response = await standardRequestWithErrorFlash.get(url);

    return response.data.custom_field_options;
  };

  fetchCounts = async (params: TalentSearchQueryParams) => {
    const response = await standardRequestWithErrorFlash.post(
      this.countsPath,
      params
    );

    return response.data;
  };

  cacheParams = async (params: TalentSearchQueryParams) => {
    if (!this.cacheParamsPath) {
      // We do not cache params for Talent Rediscovery, so this should never happen, but still
      return;
    }

    await standardRequestWithErrorFlash.put(this.cacheParamsPath, params);
  };
  fetchEducation = async (term: string, path: string) => {
    const response = await standardRequestWithErrorFlash.get(path, {
      params: {
        q: term,
        react_select: true,
      },
    });

    return response.data.results;
  };

  fetchSchools = async (term: string) => {
    return await this.fetchEducation(term, this.schoolsPath);
  };

  fetchDegrees = async (term: string) => {
    return await this.fetchEducation(term, this.degreesPath);
  };

  fetchDisciplines = async (term: string) => {
    return await this.fetchEducation(term, this.disciplinesPath);
  };

  async fetchJobKeywords(): Promise<KeywordsResponse> {
    // This route may not exist for Talent Rediscovery
    if (!this.jobKeywordsPath) {
      return { terms: [] };
    }

    const { data } = await standardRequest.get(this.jobKeywordsPath);

    return {
      terms: data.hiring_plan_search_term_list,
      error_message: data.error_message,
    };
  }

  async generateJobKeywords(): Promise<KeywordsResponse> {
    // This route may not exist for Talent Rediscovery
    if (!this.jobKeywordsPath) {
      return { terms: [] };
    }

    const { data } = await standardRequest.put(this.jobKeywordsPath);

    return {
      terms: data.hiring_plan_search_term_list,
      error_message: data.error_message,
    };
  }

  async fetchRediscoveryKeywords(
    userPrompt: string
  ): Promise<KeywordsResponse> {
    // This route will not exist for Talent Filtering
    if (!this.rediscoveryKeywordsPath) {
      return { terms: [] };
    }

    const { data } = await standardRequestWithErrorFlash.post(
      this.rediscoveryKeywordsPath,
      {
        text: userPrompt,
      }
    );

    return {
      terms: data.keywords,
      error_message: data.error_message,
    };
  }

  fetchJobs = async (term = '') => {
    const url = new URI(this.jobsPath)
      .addQuery({
        job_status: 'all',
        term,
      })
      .toString();
    const {
      data: { results },
    } = await standardRequest.get(url);

    return results;
  };

  fetchDepartments = async (term = '') => {
    const url = new URI(this.departmentsPath).addQuery({ term }).toString();
    const {
      data: { results },
    } = await standardRequest.get(url);

    return results;
  };

  fetchOffices = async (term = '') => {
    const url = new URI(this.officesPath).addQuery({ term }).toString();
    const {
      data: { results },
    } = await standardRequest.get(url);

    return results;
  };

  fetchProspectPools = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.prospectPoolsPath,
      {
        params: {
          term: term,
          grouped: true,
        },
      }
    );

    return response.data.data;
  };

  fetchProspectPoolStages = async (
    term: string,
    prospectPoolIds: number[]
  ): Promise<{ id: number; name: string }[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.prospectPoolStagesPath,
      {
        params: {
          term: term,
          include_blank: true,
          per_page: 200,
          prospect_pool_ids: prospectPoolIds,
        },
      }
    );

    return response.data.data;
  };

  fetchProspectOwners = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.prospectOwnerOptionsPath,
      {
        params: {
          term: term,
          react_select: true,
          per_page: 200,
        },
      }
    );

    return response.data.results;
  };

  fetchSources = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.sourceOptionsPath,
      {
        params: {
          term: term,
          per_page: 200,
        },
      }
    );

    return response.data.results;
  };

  fetchEvents = async (term: string): Promise<Option[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.eventOptionsPath,
      {
        params: {
          term: term,
          react_select: true,
          per_page: 200,
        },
      }
    );

    return response.data.results;
  };

  fetchStages = async (
    term = ''
  ): Promise<Array<{ text: string; id: string }>> => {
    const response = await standardRequestWithErrorFlash.get(this.stagesPath, {
      params: { term },
    });

    return response.data.data;
  };

  fetchSavedSearch = async (id: number): Promise<SearchItem> => {
    const url = new URI(`${this.savedSearchesPath}/${id}`)
      .addQuery({ feature_type: 'talent_rediscovery' })
      .toString();
    const response = await standardRequestWithErrorFlash.get(url);

    return response.data.saved_search;
  };

  fetchSavedSearchOptions = async (): Promise<FilterOptions[]> => {
    const response = await standardRequestWithErrorFlash.get(
      this.savedSearchesPath,
      {
        params: {
          feature_type: 'talent_rediscovery',
        },
      }
    );

    return response.data.options;
  };

  createSavedSearch = async (
    params: SavedSearchParams
  ): Promise<SearchItem> => {
    const response = await standardRequestWithFlash.post(
      this.savedSearchesPath,
      {
        feature_type: 'talent_rediscovery',
        ...params,
      },
      { successMessage: t('filters_saved') }
    );
    return response.data.saved_search;
  };

  updateSavedSearch = async (
    params: UpdateSavedSearchParams
  ): Promise<SearchItem> => {
    const url = new URI(`${this.savedSearchesPath}/${params.id}`)
      .addQuery({
        feature_type: 'talent_rediscovery',
      })
      .toString();
    const response = await standardRequestWithFlash.put(url, params, {
      successMessage: t('filters_renamed'),
    });

    return response.data.saved_search;
  };

  deleteSavedSearch = async (id: number): Promise<SearchItem> => {
    const url = new URI(`${this.savedSearchesPath}/${id}`)
      .addQuery({ feature_type: 'talent_rediscovery' })
      .toString();
    const response = await standardRequestWithErrorFlash.delete(url, {
      successMessage: t('filters_deleted'),
    });

    return response.data.results;
  };

  fetchScorecardAttributes = async (term: string): Promise<Item[]> => {
    if (!this.scorecardAttributesPath) {
      return [];
    }

    const params: Record<string, string> = term ? { term } : {};

    const response = await standardRequestWithErrorFlash.get(
      this.scorecardAttributesPath,
      { params }
    );

    return response.data;
  };
}

export default Api;
